package top.wcpe.allincore.ui.runtime.lifecycle

/**
 * 生命周期作用域：聚合一组 [Disposable]，在销毁时按注册逆序统一释放。
 */
class LifecycleScope {

    private val disposables = ArrayList<Disposable>()

    /** 当前是否已销毁。 */
    var isDisposed = false
        private set

    /** 当前是否处于暂停状态。 */
    var isSuspended = false
        private set

    /**
     * 注册一个可释放对象。
     *
     * @throws IllegalStateException 当作用域已 dispose 或已暂停
     */
    fun register(d: Disposable) {
        check(!isDisposed) { "LifecycleScope 已销毁，无法再注册新的 Disposable" }
        check(!isSuspended) { "LifecycleScope 已暂停，无法注册新的 Disposable" }
        disposables.add(d)
    }

    /**
     * 销毁本作用域：按注册逆序调用每个 [Disposable.dispose]，并清空列表。
     * 调用幂等，重复调用无副作用。
     */
    fun dispose() {
        if (isDisposed) return
        isDisposed = true
        // 反向遍历，符合「后注册先释放」的栈式语义
        for (i in disposables.indices.reversed()) {
            disposables[i].dispose()
        }
        disposables.clear()
    }

    /**
     * 暂停本作用域：按注册逆序遍历，对每个 [Pausable] 元素调用 [Pausable.pause]。
     * 与 [dispose] 不同，暂停后不清空 disposables 列表，后续可恢复。
     * 已 disposed 或已暂停时调用幂等，无副作用。
     */
    fun suspend() {
        if (isDisposed || isSuspended) return
        isSuspended = true
        // 反向遍历，与 dispose 保持一致的栈式语义
        for (i in disposables.indices.reversed()) {
            (disposables[i] as? Pausable)?.pause()
        }
    }

    /**
     * 恢复本作用域：按注册正序遍历，对每个 [Pausable] 元素调用 [Pausable.resume]。
     * 未暂停时调用幂等，无副作用。
     */
    fun resume() {
        if (!isSuspended) return
        isSuspended = false
        // 正序遍历，恢复时按注册顺序重启
        for (d in disposables) {
            (d as? Pausable)?.resume()
        }
    }
}
